import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Button } from '@qlub-dev/qlub-kit';
import { OptionsObject, SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';
import { ReactElement } from 'react-markdown/lib/react-markdown';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { IconButton, Slide, SlideProps } from '@mui/material';
import classNames from 'classnames';
import { timeUnix } from '../../utility/k_time';
import { useTranslation } from '../translations';
import SwipeCloseSnackbar from './SwipeCloseSnackbar';
import styles from './index.module.scss';
import './index.scss';

let lastPullToRefreshTimes = 0;

export const snackBar = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { t } = useTranslation('common');
    const topToastKey = 'qlub-top-toast-key';
    const bottomToastKey = 'qlub-bottom-toast-key';
    const topSnackbarRef = useRef<HTMLDivElement>(null);
    const bottomSnackbarRef = useRef<HTMLDivElement>(null);

    const actionButton: SnackbarAction = (key?: SnackbarKey) => (
        <Button
            onClick={() => closeSnackbar(key)}
            size="small"
            variant="text"
            color="inherit"
            sx={{
                backgroundColor: 'transparent !important',
                border: 'none !important',
                py: 0,
            }}
        >
            {t('OK')}
        </Button>
    );

    useEffect(() => {
        const onScroll = () => {
            if (lastPullToRefreshTimes !== 0 && timeUnix() - lastPullToRefreshTimes > 5) {
                lastPullToRefreshTimes = 0;
                closeSnackbar();
            }
        };

        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const triggerSnackBar = useCallback(
        (msg: string | ReactElement, options?: OptionsObject, autoHide?: boolean) => {
            lastPullToRefreshTimes = timeUnix();
            enqueueSnackbar(
                <SwipeCloseSnackbar toastKey={bottomToastKey} content={msg} snackbarRef={bottomSnackbarRef} />,
                {
                    persist: !autoHide,
                    key: bottomToastKey,
                    ref: bottomSnackbarRef,
                    action: actionButton,
                    TransitionComponent: (props: SlideProps) => (
                        <Slide {...props} direction={props.in ? 'up' : 'right'} />
                    ),
                    ...options,
                },
            );
        },
        [actionButton, enqueueSnackbar],
    );

    const triggerAlternateSnackBar = useCallback(
        (
            msg: string | ReactNode,
            icon?: ReactNode,
            variant?: 'white' | 'purple' | 'whiteWithBlackText',
            hideAction?: boolean,
        ) => {
            const message = (
                <SwipeCloseSnackbar
                    toastKey={topToastKey}
                    snackbarRef={topSnackbarRef}
                    content={
                        <div>
                            <div>
                                <span>{icon || <PersonRoundedIcon />}</span>
                                <span>{msg}</span>
                            </div>

                            {!hideAction && (
                                <IconButton className={styles.action} onClick={() => closeSnackbar(topToastKey)}>
                                    <CloseRoundedIcon />
                                </IconButton>
                            )}
                        </div>
                    }
                />
            );

            enqueueSnackbar(message, {
                variant: 'default',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
                className: classNames({
                    [styles.purpleSnackbar]: variant === 'purple',
                    [styles.snackBar]: variant === 'white',
                    [styles.whiteWithBlackText]: variant !== 'purple' && variant !== 'white',
                }),
                autoHideDuration: 7000,
                key: topToastKey,
                ref: topSnackbarRef,
                TransitionComponent: (props: SlideProps) => (
                    <Slide {...props} direction={props.in ? 'down' : 'right'} />
                ),
            });
        },
        [closeSnackbar, enqueueSnackbar],
    );

    return {
        triggerSnackBar,
        closeSnackbar,
        actionButton,
        triggerAlternateSnackBar,
    };
};
